// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings > Notifications > Pop-ups inside Konvo (design/assistant-everywhere t5). Which
// messages pop up while Konvo is in front, whether Konvo's draft is offered, holding them while
// you type and keeping quiet in calls. Kept on this device and shared between windows through the
// storage event. Which sources may notify at all stays with the Sources switches.

import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {makeAutoObservable} from 'mobx';

/** off: never, the OS notification as before. direct: private chats and mentions. all: whatever would notify. */
export type InAppPopupMode = 'off' | 'direct' | 'all';

export const IN_APP_POPUP_MODES: ReadonlyArray<InAppPopupMode> = ['off', 'direct', 'all'];

export interface InAppPopupPrefs {
	mode: InAppPopupMode;
	showDraft: boolean;
	waitWhileTyping: boolean;
	quietInCalls: boolean;
}

const STORAGE_KEY = 'konvo_popups.settings';

export const DEFAULT_IN_APP_POPUP_PREFS: InAppPopupPrefs = {
	mode: 'direct',
	showDraft: true,
	waitWhileTyping: true,
	quietInCalls: true,
};

/** What was stored, with anything missing or unknown back at its default. */
export function parseInAppPopupPrefs(raw: string | null | undefined): InAppPopupPrefs {
	if (!raw) return {...DEFAULT_IN_APP_POPUP_PREFS};
	try {
		const value = JSON.parse(raw) as Partial<Record<keyof InAppPopupPrefs, unknown>>;
		const flag = (key: 'showDraft' | 'waitWhileTyping' | 'quietInCalls') =>
			typeof value[key] === 'boolean' ? (value[key] as boolean) : DEFAULT_IN_APP_POPUP_PREFS[key];
		return {
			mode: IN_APP_POPUP_MODES.includes(value.mode as InAppPopupMode)
				? (value.mode as InAppPopupMode)
				: DEFAULT_IN_APP_POPUP_PREFS.mode,
			showDraft: flag('showDraft'),
			waitWhileTyping: flag('waitWhileTyping'),
			quietInCalls: flag('quietInCalls'),
		};
	} catch {
		return {...DEFAULT_IN_APP_POPUP_PREFS};
	}
}

function read(): InAppPopupPrefs {
	try {
		return parseInAppPopupPrefs(getProtectedLocalStorage()?.getItem(STORAGE_KEY));
	} catch {
		return {...DEFAULT_IN_APP_POPUP_PREFS};
	}
}

class InAppPopupSettingsStore {
	prefs: InAppPopupPrefs = read();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		if (typeof window !== 'undefined') {
			window.addEventListener('storage', (event) => {
				if (event.key === STORAGE_KEY) this.prefs = read();
			});
		}
	}

	get mode(): InAppPopupMode {
		return this.prefs.mode;
	}

	get showDraft(): boolean {
		return this.prefs.showDraft;
	}

	get waitWhileTyping(): boolean {
		return this.prefs.waitWhileTyping;
	}

	get quietInCalls(): boolean {
		return this.prefs.quietInCalls;
	}

	set(patch: Partial<InAppPopupPrefs>): void {
		this.prefs = {...this.prefs, ...patch};
		try {
			getProtectedLocalStorage()?.setItem(STORAGE_KEY, JSON.stringify(this.prefs));
		} catch {
			// Storage may be unavailable; the change still holds for this window.
		}
	}
}

export default new InAppPopupSettingsStore();
